import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useSignals } from "@preact/signals-react/runtime";
import { addMonths, endOfMonth, format, getWeek, isAfter, isBefore, parseISO, startOfMonth, startOfWeek, } from "date-fns";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@ingka/button";
import InputField from "@ingka/input-field";
import SSRIcon from "@ingka/ssr-icon";
import calendar from "@ingka/ssr-icon/paths/calendar";
import CheckIcon from "@ingka/ssr-icon/paths/checkmark";
import ChevronLeftIcon from "@ingka/ssr-icon/paths/chevron-left-small";
import ChevronRightIcon from "@ingka/ssr-icon/paths/chevron-right-small";
import Text from "@ingka/text";
import { PopOver } from "@pdpp/lib-react";
import { weekStartsOn$ } from "../../state/weekStartsOn";
import { computeForecastBounds, doesMonthOverlapForecastRange, generateCalendarMonth, getFiscalYearFullFromDate, getFiscalYearSelectionDate, getWeekdayLabels, isDateWithinForecastRange, } from "../../utils/forecastDateUtils";
import "./DatePicker.css";
const toYmd = (date) => format(date, "yyyy-MM-dd");
export const DatePicker = memo(function DatePicker$({ mode, selectedDate, onSelect, onClose, anchorEl, onScroll, horizontalAlignment = "right", horizontalPosition = "right", verticalAlignment = "top", verticalPosition = "bottom", clickAwayHandler, minDate, maxDate, }) {
    useSignals();
    const { i18n: i18nInstance } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState(i18nInstance.language);
    useEffect(() => {
        const handleLanguageChange = (lng) => {
            setCurrentLanguage(lng);
        };
        i18nInstance.on("languageChanged", handleLanguageChange);
        return () => {
            i18nInstance.off("languageChanged", handleLanguageChange);
        };
    }, [i18nInstance]);
    const weekStartsOnValue = weekStartsOn$.value;
    const anchorDate = useMemo(() => parseISO(selectedDate), [selectedDate]);
    const fiscalYears = useMemo(() => computeForecastBounds().allowedFiscalYears, []);
    const weekStart = useMemo(() => {
        return startOfWeek(anchorDate);
    }, [anchorDate]);
    const [viewMonth, setViewMonth] = useState(anchorDate);
    const [hoveredWeekStart, setHoveredWeekStart] = useState(null);
    useEffect(() => {
        setViewMonth(anchorDate);
    }, [anchorDate]);
    const monthGrid = useMemo(() => {
        return generateCalendarMonth(viewMonth);
    }, [viewMonth]);
    const monthLabel = useMemo(() => format(viewMonth, "MMMM yyyy"), [viewMonth]);
    const weekdayLabels = useMemo(() => {
        return getWeekdayLabels(weekStartsOnValue, currentLanguage);
    }, [weekStartsOnValue, currentLanguage]);
    const isDateAllowed = useCallback((dateStr) => {
        if (minDate != null || maxDate != null) {
            const date = parseISO(dateStr);
            if (minDate != null && isBefore(date, parseISO(minDate))) {
                return false;
            }
            if (maxDate != null && isAfter(date, parseISO(maxDate))) {
                return false;
            }
            return true;
        }
        return isDateWithinForecastRange(dateStr);
    }, [minDate, maxDate]);
    const isMonthAllowed = useCallback((dateInMonth) => {
        if (minDate != null || maxDate != null) {
            const monthStart = startOfMonth(dateInMonth);
            const monthEnd = endOfMonth(dateInMonth);
            const min = minDate != null ? parseISO(minDate) : null;
            const max = maxDate != null ? parseISO(maxDate) : null;
            if (min != null && isBefore(monthEnd, min))
                return false;
            if (max != null && isAfter(monthStart, max))
                return false;
            return true;
        }
        return doesMonthOverlapForecastRange(dateInMonth);
    }, [minDate, maxDate]);
    const handlePrevMonth = () => {
        setViewMonth((prev) => addMonths(prev, -1));
    };
    const handleNextMonth = () => {
        setViewMonth((prev) => addMonths(prev, 1));
    };
    const handleDayClick = (date) => {
        if (mode === "week") {
            const weekStartDate = startOfWeek(date);
            const weekStartStr = toYmd(weekStartDate);
            if (!isDateAllowed(weekStartStr)) {
                return;
            }
            onSelect(weekStartStr);
        }
        else {
            const dateStr = toYmd(date);
            if (!isDateAllowed(dateStr)) {
                return;
            }
            onSelect(dateStr);
        }
        onClose();
    };
    const renderContent = () => {
        if (mode === "year") {
            const selectedYear = getFiscalYearFullFromDate(anchorDate);
            return (_jsx("div", { className: "forecast-datepicker forecast-datepicker--year", children: _jsx("div", { className: "forecast-datepicker__year-list", children: fiscalYears.map((fy) => {
                        const isSelected = fy === selectedYear;
                        return (_jsxs("span", { role: "button", tabIndex: 0, "aria-label": `FY ${fy}`, "aria-selected": isSelected, className: `forecast-datepicker__year ${isSelected ? "is-selected" : ""}`, onClick: () => {
                                const dateStr = getFiscalYearSelectionDate(fy);
                                onSelect(dateStr);
                                onClose();
                            }, onKeyDown: (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    const dateStr = getFiscalYearSelectionDate(fy);
                                    onSelect(dateStr);
                                    onClose();
                                }
                            }, children: [_jsxs(Text, { textStyle: "Body.M", children: ["FY ", fy] }), isSelected && (_jsx(SSRIcon, { paths: CheckIcon, className: "forecast-datepicker__year-check" }))] }, fy));
                    }) }) }));
        }
        const viewMonthNum = viewMonth.getMonth();
        const viewYearNum = viewMonth.getFullYear();
        const isPrevMonthDisabled = !isMonthAllowed(addMonths(viewMonth, -1));
        const isNextMonthDisabled = !isMonthAllowed(addMonths(viewMonth, 1));
        return (_jsxs("div", { className: `forecast-datepicker ${mode === "week" ? "forecast-datepicker--week" : ""}`, children: [_jsxs("div", { className: "forecast-datepicker__header", children: [_jsx(Button, { type: "tertiary", size: "small", iconOnly: true, ssrIcon: ChevronLeftIcon, disabled: isPrevMonthDisabled, onClick: handlePrevMonth, "aria-label": "Previous month" }), _jsx(Text, { textStyle: "Heading.S", className: "forecast-datepicker__month", children: monthLabel }), _jsx(Button, { type: "tertiary", size: "small", iconOnly: true, ssrIcon: ChevronRightIcon, disabled: isNextMonthDisabled, onClick: handleNextMonth, "aria-label": "Next month" })] }), weekdayLabels.length > 0 && (_jsxs("div", { className: "forecast-datepicker__weekdays", children: [_jsx("div", { className: "forecast-datepicker__weekday-spacer" }), weekdayLabels.map((day) => (_jsx(Text, { textStyle: "Label.S", className: "forecast-datepicker__weekday", children: day }, day)))] })), _jsx("div", { className: "forecast-datepicker__grid", role: "grid", "aria-label": `${monthLabel} calendar`, children: Array.from({ length: Math.ceil(monthGrid.length / 7) }, (_, weekIndex) => {
                        const weekStartIndex = weekIndex * 7;
                        const weekDates = monthGrid.slice(weekStartIndex, weekStartIndex + 7);
                        const firstDate = weekDates[0];
                        if (firstDate === undefined) {
                            return [];
                        }
                        if (!weekDates.some((d) => d.getMonth() === viewMonthNum &&
                            d.getFullYear() === viewYearNum)) {
                            return [];
                        }
                        const dateWeekStart = startOfWeek(firstDate);
                        const weekNumber = getWeek(dateWeekStart);
                        const isSelectedWeekForDayView = mode === "day" &&
                            weekDates.some((date) => toYmd(date) === toYmd(anchorDate));
                        const isSelectedWeekForWeekView = mode === "week" && toYmd(dateWeekStart) === toYmd(weekStart);
                        const isSelectedWeek = isSelectedWeekForDayView || isSelectedWeekForWeekView;
                        const isHoveredWeek = mode === "week" &&
                            hoveredWeekStart !== null &&
                            toYmd(dateWeekStart) === toYmd(hoveredWeekStart);
                        const weeknumCellClassName = [
                            "forecast-datepicker__weeknum-cell",
                            (isSelectedWeek || isHoveredWeek) && "is-selected-week-row",
                        ]
                            .filter(Boolean)
                            .join(" ");
                        const weekItems = [
                            _jsx("div", { className: weeknumCellClassName, role: "rowheader", "aria-label": `Week ${weekNumber}`, onMouseEnter: () => {
                                    if (mode === "week") {
                                        setHoveredWeekStart(dateWeekStart);
                                    }
                                }, onMouseLeave: () => {
                                    if (mode === "week") {
                                        setHoveredWeekStart(null);
                                    }
                                }, children: _jsx(Text, { textStyle: "Body.S", className: "forecast-datepicker__weeknum", children: weekNumber }) }, `week-${weekIndex}`),
                        ];
                        weekItems.push(...weekDates.map((date, dayIndex) => {
                            const inMonth = date.getMonth() === viewMonthNum &&
                                date.getFullYear() === viewYearNum;
                            const dateStr = toYmd(date);
                            const isOutOfDayRange = !isDateAllowed(dateStr);
                            const cellWeekStart = startOfWeek(date);
                            const cellWeekStartStr = toYmd(cellWeekStart);
                            const isOutOfWeekRange = !isDateAllowed(cellWeekStartStr);
                            const isSelectedDay = mode === "day" && toYmd(date) === toYmd(anchorDate);
                            const isSelectedWeekForDate = mode === "week" && isSelectedWeek;
                            const isHoveredWeekForDate = mode === "week" && isHoveredWeek;
                            const isFirstDay = dayIndex === 0;
                            const isLastDay = dayIndex === weekDates.length - 1;
                            const isWeekSelected = isSelectedWeekForDate || isHoveredWeekForDate;
                            const isIntermediateDay = isWeekSelected && !isFirstDay && !isLastDay;
                            const cellClassName = [
                                "forecast-datepicker__cell",
                                isWeekSelected && "is-selected-week",
                                isWeekSelected && mode === "week" && "is-hovered-week",
                                isIntermediateDay && "is-intermediate-day-of-week",
                                isIntermediateDay &&
                                    dayIndex === 1 &&
                                    "is-first-intermediate-day",
                                isIntermediateDay &&
                                    dayIndex === weekDates.length - 2 &&
                                    "is-last-intermediate-day",
                                !inMonth && "is-out",
                                isFirstDay && "is-first-day-in-week-row",
                                isLastDay && "is-last-day-in-week-row",
                            ]
                                .filter(Boolean)
                                .join(" ");
                            const dayClassName = [
                                "forecast-datepicker__day",
                                isSelectedDay && "is-selected",
                                isWeekSelected && isFirstDay && "is-first-day-of-week",
                                isWeekSelected && isLastDay && "is-last-day-of-week",
                                !inMonth && "is-out",
                                mode === "week" &&
                                    isWeekSelected &&
                                    !isFirstDay &&
                                    !isLastDay &&
                                    "is-week-intermediate-day",
                            ]
                                .filter(Boolean)
                                .join(" ");
                            return (_jsx("div", { className: cellClassName, role: "gridcell", onMouseEnter: () => {
                                    if (mode === "week") {
                                        setHoveredWeekStart(dateWeekStart);
                                    }
                                }, onMouseLeave: () => {
                                    if (mode === "week") {
                                        setHoveredWeekStart(null);
                                    }
                                }, children: _jsx(Button, { type: "ghost", size: "small", className: dayClassName, "aria-label": format(date, "EEEE, MMMM d, yyyy"), "aria-selected": mode === "day" ? isSelectedDay : isSelectedWeekForDate, disabled: mode === "day" ? isOutOfDayRange : isOutOfWeekRange, onClick: () => handleDayClick(date), onFocus: () => {
                                        if (mode === "week") {
                                            setHoveredWeekStart(dateWeekStart);
                                        }
                                    }, onBlur: () => {
                                        if (mode === "week") {
                                            setHoveredWeekStart(null);
                                        }
                                    }, children: _jsx(Text, { textStyle: "Body.M", className: "forecast-datepicker__day-text", children: date.getDate() }) }) }, date.toISOString()));
                        }));
                        return weekItems;
                    }).flat() })] }));
    };
    if (anchorEl === null || anchorEl === undefined) {
        return _jsx(_Fragment, {});
    }
    return (_jsx(PopOver, { anchorEl: anchorEl, clickAwayHandler: clickAwayHandler, horizontalAlignment: horizontalAlignment, horizontalPosition: horizontalPosition, verticalAlignment: verticalAlignment, verticalPosition: verticalPosition, onScroll: onScroll, children: renderContent() }));
});
export const DateRangePicker = memo(function DateRangePicker$({ fromDate, toDate, minDate, maxDate, onFromDateChange, onToDateChange, }) {
    const { t } = useTranslation();
    const [showFromPicker, setShowFromPicker] = useState(false);
    const [showToPicker, setShowToPicker] = useState(false);
    const fromInputRef = useRef(null);
    const toInputRef = useRef(null);
    const toMinDate = fromDate && fromDate !== "" ? fromDate : minDate;
    const toggleFromPicker = useCallback(() => {
        setShowFromPicker((v) => !v);
    }, []);
    const toggleToPicker = useCallback(() => {
        setShowToPicker((v) => !v);
    }, []);
    const closeFromPicker = useCallback(() => {
        setShowFromPicker(false);
    }, []);
    const closeToPicker = useCallback(() => {
        setShowToPicker(false);
    }, []);
    const handleFromSelect = useCallback((date) => {
        onFromDateChange(date);
        if (toDate &&
            toDate !== "" &&
            isBefore(parseISO(toDate), parseISO(date))) {
            onToDateChange(date);
        }
        closeFromPicker();
    }, [closeFromPicker, onFromDateChange, onToDateChange, toDate]);
    const handleToSelect = useCallback((date) => {
        onToDateChange(date);
        closeToPicker();
    }, [closeToPicker, onToDateChange]);
    return (_jsxs("div", { className: "forecast-publish-modal__section forecast-publish-modal__section--dates", children: [_jsx(Text, { textStyle: "Body.M", tagName: "div", children: _jsx("strong", { children: t("publishModal.period") }) }), _jsxs("div", { className: "forecast-publish-modal__date-pickers-wrapper", children: [_jsxs("div", { className: "forecast-publish-modal__date-picker-wrapper", ref: fromInputRef, children: [_jsx(InputField, { value: fromDate ? format(parseISO(fromDate), "d MMM yyyy") : "", id: "from-date-input", type: "text", label: t("publishModal.from"), readOnly: true, onClick: toggleFromPicker }), _jsx("div", { className: "forecast-publish-modal__date-picker-icon", onClick: toggleFromPicker, children: _jsx(SSRIcon, { paths: calendar }) }), showFromPicker && fromInputRef.current && (_jsx(DatePicker, { mode: "day", selectedDate: fromDate, onSelect: handleFromSelect, onClose: closeFromPicker, anchorEl: fromInputRef.current, clickAwayHandler: closeFromPicker, horizontalAlignment: "left", horizontalPosition: "left", minDate: minDate, maxDate: maxDate }))] }), _jsxs("div", { className: "forecast-publish-modal__date-picker-wrapper", ref: toInputRef, children: [_jsx(InputField, { value: toDate ? format(parseISO(toDate), "d MMM yyyy") : "", id: "to-date-input", type: "text", label: t("publishModal.to"), readOnly: true, onClick: toggleToPicker }), _jsx("div", { className: "forecast-publish-modal__date-picker-icon", onClick: toggleToPicker, children: _jsx(SSRIcon, { paths: calendar }) }), showToPicker && toInputRef.current && (_jsx(DatePicker, { mode: "day", selectedDate: toDate, onSelect: handleToSelect, onClose: closeToPicker, horizontalAlignment: "left", horizontalPosition: "left", anchorEl: toInputRef.current, clickAwayHandler: closeToPicker, minDate: toMinDate, maxDate: maxDate }))] })] })] }));
});
//# sourceMappingURL=DatePicker.js.map